import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import './Signup.css';
import Dashboard from '../Dashboard';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],
  ['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],
  ['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],
  ['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],
  ['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],
  ['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],
  ['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],
  ['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],
  ['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],
  ['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],
  ['WI','Wisconsin'],['WY','Wyoming'],['DC','District of Columbia'],
];

const LICENSE_TYPES = [
  { value: 'PA',         label: 'Public Adjuster',       desc: 'Licensed public adjuster representing policyholders' },
  { value: 'IA',         label: 'Independent Adjuster', desc: 'Independent appraiser representing carriers' },
  { value: 'Contractor', label: 'Contractor',            desc: 'Licensed contractor serving as appraiser or umpire' },
  { value: 'Attorney',   label: 'Attorney',              desc: 'Licensed attorney serving as appraiser or umpire' },
  { value: 'Engineer',   label: 'Engineer',              desc: 'Licensed engineer serving as appraiser or umpire' },
];

const PRIMARY_ROLES = [
  { value: 'Appraiser', label: 'Appraiser', desc: 'I primarily work as an appraiser on claims' },
  { value: 'Umpire',    label: 'Umpire',    desc: 'I primarily serve as a neutral umpire' },
  { value: 'Both',      label: 'Both',      desc: 'I work as both appraiser and umpire' },
];

const SIDES = [
  { value: 'Policyholder', label: 'Insured side',  desc: 'I normally appraise on behalf of the insured' },
  { value: 'Carrier',      label: 'Carrier side',  desc: 'I normally appraise on behalf of the carrier' },
  { value: 'Both',         label: 'Both',          desc: 'I appraise on both sides' },
];

const NEEDS_SIDE    = ['Contractor', 'Attorney', 'Engineer'];
const getSteps = (licenseType) => NEEDS_SIDE.includes(licenseType)
  ? ['Identity', 'License', 'Side', 'Role', 'State', 'Attest']
  : ['Identity', 'License', 'Role', 'State', 'Attest'];

// ─── PROGRESS BAR ────────────────────────────────────────────────────────────
const ProgressBar = ({ steps, currentStep }) => (
  <div className="signup-progress">
    <div className="signup-progress-track">
      {steps.map((label, i) => (
        <div key={label} className={`signup-progress-step ${i < currentStep ? 'completed' : i === currentStep ? 'active' : ''}`}>
          <div className="signup-progress-dot">{i < currentStep ? '✓' : i + 1}</div>
          <div className="signup-progress-label">{label}</div>
        </div>
      ))}
    </div>
  </div>
);

// ─── OPTION CARD ─────────────────────────────────────────────────────────────
const OptionCard = ({ option, selected, onSelect }) => (
  <div className={`signup-option ${selected ? 'selected' : ''}`} onClick={() => onSelect(option.value)}>
    <div className="signup-option-radio">
      {selected && <div className="signup-option-radio-dot" />}
    </div>
    <div className="signup-option-text">
      <div className="signup-option-label">{option.label}</div>
      {option.desc && <div className="signup-option-desc">{option.desc}</div>}
    </div>
  </div>
);

// ─── STEP 1: IDENTITY ────────────────────────────────────────────────────────
const StepIdentity = ({ data, onChange, onNext, error, loading }) => {
  const [errors, setErrors] = useState({});

  const SUFFIXES = ['', 'Jr.', 'Sr.', 'II', 'III', 'IV', 'Esq.', 'CPA', 'PhD'];

  const validate = () => {
    const e = {};
    if (!data.firstName?.trim())           e.firstName       = 'First name is required';
    if (!data.lastName?.trim())            e.lastName        = 'Last name is required';
    if (!data.email.trim())                e.email           = 'Email is required';
    if (!/\S+@\S+\.\S+/.test(data.email)) e.email           = 'Enter a valid email address';
    if (data.email !== data.confirmEmail)  e.confirmEmail    = 'Emails do not match';
    if (!data.password)                    e.password        = 'Password is required';
    if (data.password.length < 8)         e.password        = 'Password must be at least 8 characters';
    if (!/[a-z]/.test(data.password))     e.password        = 'Password must include a lowercase letter';
    if (!/[A-Z]/.test(data.password))     e.password        = 'Password must include an uppercase letter';
    if (!/[0-9]/.test(data.password))     e.password        = 'Password must include a number';
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|<>?,./`~]/.test(data.password)) e.password = 'Password must include a special character';
    if (data.password !== data.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <>
      <div className="signup-step-eyebrow">Step 1 of signup</div>
      <h2>Create your account</h2>
      <p className="signup-card-sub">Use your professional work email — the one associated with your license.</p>
      {error && <div className="signup-error">{error}</div>}

      {/* Name row — first + last side by side */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <div className="signup-field" style={{ flex: 1 }}>
          <label className="signup-label">First name <span>*</span></label>
          <input
            className={`signup-input ${errors.firstName ? 'error' : ''}`}
            type="text"
            placeholder="First"
            value={data.firstName || ''}
            onChange={e => onChange('firstName', e.target.value)}
          />
          {errors.firstName && <div className="signup-field-error">{errors.firstName}</div>}
        </div>
        <div className="signup-field" style={{ flex: 1 }}>
          <label className="signup-label">Last name <span>*</span></label>
          <input
            className={`signup-input ${errors.lastName ? 'error' : ''}`}
            type="text"
            placeholder="Last"
            value={data.lastName || ''}
            onChange={e => onChange('lastName', e.target.value)}
          />
          {errors.lastName && <div className="signup-field-error">{errors.lastName}</div>}
        </div>
      </div>

      {/* Middle name + suffix row */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <div className="signup-field" style={{ flex: 2 }}>
          <label className="signup-label">Middle name <span style={{ color: 'var(--muted)', fontWeight: 300 }}>(optional)</span></label>
          <input
            className="signup-input"
            type="text"
            placeholder="Middle"
            value={data.middleName || ''}
            onChange={e => onChange('middleName', e.target.value)}
          />
        </div>
        <div className="signup-field" style={{ flex: 1 }}>
          <label className="signup-label">Suffix <span style={{ color: 'var(--muted)', fontWeight: 300 }}>(optional)</span></label>
          <select
            className="signup-input"
            value={data.nameSuffix || ''}
            onChange={e => onChange('nameSuffix', e.target.value)}
          >
            {SUFFIXES.map(s => <option key={s} value={s}>{s || '—'}</option>)}
          </select>
        </div>
      </div>

      <div className="signup-field">
        <label className="signup-label">Work email <span>*</span></label>
        <input
          className={`signup-input ${errors.email ? 'error' : ''}`}
          type="email"
          placeholder="your@firm.com"
          value={data.email}
          onChange={e => onChange('email', e.target.value)}
        />
        {errors.email && <div className="signup-field-error">{errors.email}</div>}
      </div>

      <div className="signup-field">
        <label className="signup-label">Confirm email <span>*</span></label>
        <input
          className={`signup-input ${errors.confirmEmail ? 'error' : ''}`}
          type="email"
          placeholder="Re-enter your email"
          value={data.confirmEmail}
          onChange={e => onChange('confirmEmail', e.target.value)}
        />
        {errors.confirmEmail && <div className="signup-field-error">{errors.confirmEmail}</div>}
      </div>

      <div className="signup-field">
        <label className="signup-label">Password <span>*</span></label>
        <input
          className={`signup-input ${errors.password ? 'error' : ''}`}
          type="password"
          placeholder="Min 8 chars, upper, lower, number, special (!@#$...)"
          value={data.password}
          onChange={e => onChange('password', e.target.value)}
        />
        {errors.password && <div className="signup-field-error">{errors.password}</div>}
      </div>

      <div className="signup-field">
        <label className="signup-label">Confirm password <span>*</span></label>
        <input
          className={`signup-input ${errors.confirmPassword ? 'error' : ''}`}
          type="password"
          placeholder="Re-enter your password"
          value={data.confirmPassword || ''}
          onChange={e => onChange('confirmPassword', e.target.value)}
        />
        {errors.confirmPassword && <div className="signup-field-error">{errors.confirmPassword}</div>}
      </div>

      <div className="signup-btn-row">
        <button className="signup-btn-primary" onClick={() => { if (validate()) onNext(); }} disabled={loading}>
          {loading ? 'Checking…' : 'Continue'}
        </button>
      </div>
    </>
  );
};

// ─── STEP 2: LICENSE TYPE ────────────────────────────────────────────────────
const StepLicenseType = ({ data, onChange, onNext, onBack }) => {
  const [error, setError] = useState('');
  return (
    <>
      <div className="signup-step-eyebrow">Step 2 of signup</div>
      <h2>Your license type</h2>
      <p className="signup-card-sub">Select your primary professional license.</p>
      {error && <div className="signup-error">{error}</div>}
      <div className="signup-options">
        {LICENSE_TYPES.map(opt => <OptionCard key={opt.value} option={opt} selected={data.licenseType === opt.value} onSelect={v => onChange('licenseType', v)} />)}
      </div>
      <div className="signup-btn-row" style={{ marginTop: '24px' }}>
        <button className="signup-btn-back" onClick={onBack}>Back</button>
        <button className="signup-btn-primary" onClick={() => { if (!data.licenseType) { setError('Please select your license type'); return; } setError(''); onNext(); }}>Continue</button>
      </div>
    </>
  );
};

// ─── STEP: SIDE ──────────────────────────────────────────────────────────────
const StepSide = ({ data, onChange, onNext, onBack }) => {
  const [error, setError] = useState('');
  return (
    <>
      <div className="signup-step-eyebrow">Step 3 of signup</div>
      <h2>Which side do you appraise?</h2>
      <p className="signup-card-sub">Do you normally appraise insured or carrier side?</p>
      {error && <div className="signup-error">{error}</div>}
      <div className="signup-options">
        {SIDES.map(opt => <OptionCard key={opt.value} option={opt} selected={data.side === opt.value} onSelect={v => onChange('side', v)} />)}
      </div>
      <div className="signup-btn-row" style={{ marginTop: '24px' }}>
        <button className="signup-btn-back" onClick={onBack}>Back</button>
        <button className="signup-btn-primary" onClick={() => { if (!data.side) { setError('Please select which side you appraise'); return; } setError(''); onNext(); }}>Continue</button>
      </div>
    </>
  );
};

// ─── STEP: ROLE ──────────────────────────────────────────────────────────────
const StepRole = ({ data, onChange, onNext, onBack, stepNum }) => {
  const [error, setError] = useState('');
  return (
    <>
      <div className="signup-step-eyebrow">Step {stepNum} of signup</div>
      <h2>Your primary role</h2>
      <p className="signup-card-sub">What is your primary role in the appraisal process?</p>
      {error && <div className="signup-error">{error}</div>}
      <div className="signup-options">
        {PRIMARY_ROLES.map(opt => <OptionCard key={opt.value} option={opt} selected={data.primaryRole === opt.value} onSelect={v => onChange('primaryRole', v)} />)}
      </div>
      <div className="signup-btn-row" style={{ marginTop: '24px' }}>
        <button className="signup-btn-back" onClick={onBack}>Back</button>
        <button className="signup-btn-primary" onClick={() => { if (!data.primaryRole) { setError('Please select your primary role'); return; } setError(''); onNext(); }}>Continue</button>
      </div>
    </>
  );
};

// ─── STEP: STATE ─────────────────────────────────────────────────────────────
const StepState = ({ data, onChange, onNext, onBack, stepNum }) => {
  const [error, setError] = useState('');
  return (
    <>
      <div className="signup-step-eyebrow">Step {stepNum} of signup</div>
      <h2>Your home state</h2>
      <p className="signup-card-sub">The state where your primary license is issued.</p>
      {error && <div className="signup-error">{error}</div>}
      <div className="signup-field">
        <label className="signup-label">Home state <span>*</span></label>
        <select className="signup-select" value={data.homeState} onChange={e => onChange('homeState', e.target.value)}>
          <option value="">Select your state...</option>
          {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </select>
      </div>
      <div className="signup-btn-row">
        <button className="signup-btn-back" onClick={onBack}>Back</button>
        <button className="signup-btn-primary" onClick={() => { if (!data.homeState) { setError('Please select your home state'); return; } setError(''); onNext(); }}>Continue</button>
      </div>
    </>
  );
};

// ─── STEP: ATTEST ────────────────────────────────────────────────────────────
const StepAttest = ({ data, onBack, onSubmit, loading, stepNum, submitError }) => {
  const [agreed, setAgreed] = useState(false);
  const [error,  setError]  = useState('');
  const sideLabel = { Policyholder: 'Insured side', Carrier: 'Carrier side', Both: 'Both sides' };
  const displayError = submitError || error;
  return (
    <>
      <div className="signup-step-eyebrow">Final step</div>
      <h2>Review & confirm</h2>
      <p className="signup-card-sub">Please review your information and confirm before creating your account.</p>
      {displayError && <div className="signup-error">{displayError}</div>}
      <div className="review-summary">
        <div className="review-row"><span className="review-row-label">Name</span><span className="review-row-value">{data.fullName}</span></div>
        <div className="review-row"><span className="review-row-label">Email</span><span className="review-row-value">{data.email}</span></div>
        <div className="review-row"><span className="review-row-label">License type</span><span className="review-row-value">{data.licenseType}</span></div>
        {data.side && <div className="review-row"><span className="review-row-label">Appraises</span><span className="review-row-value">{sideLabel[data.side] || data.side}</span></div>}
        <div className="review-row"><span className="review-row-label">Primary role</span><span className="review-row-value">{data.primaryRole}</span></div>
        <div className="review-row"><span className="review-row-label">Home state</span><span className="review-row-value">{data.homeState}</span></div>
      </div>
      <div className="attestation-box">
        <div className="attestation-warning">⚠ Attestation required</div>
        <div className="attestation-text">
          By creating this account you confirm that all information provided is accurate and matches your professional license records.
          You agree to UmpireIQ's Terms of Service and Privacy Policy. Providing false information may result in immediate account
          termination and may constitute professional misconduct reportable to your licensing authority.
        </div>
        <label className="attestation-checkbox">
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
          <span className="attestation-checkbox-label">I confirm this information is accurate and I agree to the Terms of Service and Privacy Policy.</span>
        </label>
      </div>
      <div className="signup-btn-row">
        <button className="signup-btn-back" onClick={onBack} disabled={loading}>Back</button>
        <button className="signup-btn-primary" onClick={() => { if (!agreed) { setError('You must confirm the attestation to continue'); return; } setError(''); onSubmit(); }} disabled={loading || !agreed}>
          {loading ? 'Creating account...' : 'Create account'}
        </button>
      </div>
    </>
  );
};

// ─── CLAIM PROFILE MODAL ─────────────────────────────────────────────────────
const ClaimProfileModal = ({ profile, onClaim, onSkip }) => (
  <div className="signup-modal-overlay">
    <div className="signup-modal">
      <div className="signup-modal-header">
        <div className="signup-step-eyebrow" style={{ marginBottom: 0 }}>Existing profile found</div>
        <h2 style={{ margin: '12px 0 0', fontSize: '22px' }}>Claim your profile?</h2>
      </div>
      <div className="signup-modal-body">
        <p style={{ fontSize: '15px', color: 'var(--muted)', lineHeight: 1.7, fontWeight: 300, margin: '0 0 12px' }}>
          We found an existing UmpireIQ profile for{' '}
          <strong style={{ color: 'var(--foreground)' }}>{profile.display_name}</strong>{' '}
          associated with this email address.
        </p>
        <p style={{ fontSize: '15px', color: 'var(--muted)', lineHeight: 1.7, fontWeight: 300, margin: '0 0 28px' }}>
          Would you like to claim it? We'll send a confirmation link to your email — click it to merge your new account with the existing profile, including any ratings and history.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button className="signup-btn-primary" onClick={onClaim}>Yes, claim this profile</button>
          <button
            onClick={onSkip}
            style={{ background: 'none', border: 'none', fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--muted)', cursor: 'pointer', padding: '10px', textAlign: 'center' }}
          >
            No, create a new profile
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── WELCOME MODAL ───────────────────────────────────────────────────────────
const WelcomeModal = ({ onGetVerified, onDismiss }) => (
  <div className="signup-modal-overlay">
    <div className="signup-modal">
      <div className="signup-modal-header">
        <div className="signup-step-eyebrow" style={{ marginBottom: 0 }}>Welcome to UmpireIQ</div>
        <h2 style={{ margin: '12px 0 0', fontSize: '26px' }}>You're in!</h2>
      </div>
      <div className="signup-modal-body">
        <p style={{ fontSize: '15px', color: 'var(--muted)', lineHeight: 1.7, fontWeight: 300, margin: '0 0 28px' }}>
          Your account is created. You can browse and recommend umpires right away. To rate umpires or leave recommendations, you'll need to verify your license first.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button className="signup-btn-primary" onClick={onGetVerified}>Get Verified</button>
          <button
            onClick={onDismiss}
            style={{ background: 'none', border: 'none', fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--muted)', cursor: 'pointer', padding: '10px', textAlign: 'center' }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── MAIN SIGNUP ─────────────────────────────────────────────────────────────
const Signup = ({ onNavigate }) => {
  const [currentStep,       setCurrentStep]       = useState(0);
  const [showWelcomeModal,  setShowWelcomeModal]  = useState(false);
  const [submitting,        setSubmitting]        = useState(false);
  const [submitError,       setSubmitError]       = useState('');
  const [nominatedProfile,  setNominatedProfile]  = useState(null);
  const [showClaimModal,    setShowClaimModal]    = useState(false);
  const [isClaimingProfile, setIsClaimingProfile] = useState(false);
  const [showClaimEmailSent,setShowClaimEmailSent]= useState(false);
  const [checkingEmail,     setCheckingEmail]     = useState(false);

  const [formData, setFormData] = useState({
    fullName: '', email: '', confirmEmail: '', password: '',
    licenseType: '', side: '', primaryRole: '', homeState: '', firstName: '',
    middleName: '', lastName: '', nameSuffix: '', confirmPassword: '',
  });

  const updateField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const needsSide   = NEEDS_SIDE.includes(formData.licenseType);
  const steps       = getSteps(formData.licenseType);
  const next        = () => setCurrentStep(s => Math.min(s + 1, steps.length - 1));
  const back        = () => setCurrentStep(s => Math.max(s - 1, 0));

  const handleIdentityNext = async () => {
    setCheckingEmail(true);
    try {
      const { data } = await supabase.rpc('check_nomination_by_email', {
        p_email: formData.email.trim().toLowerCase(),
      });
      if (data && data.length > 0) {
        setNominatedProfile(data[0]);
        setShowClaimModal(true);
      } else {
        next();
      }
    } catch {
      next();
    } finally {
      setCheckingEmail(false);
    }
  };

  // ─── SUBMIT ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    try {
      // Step 1 — create auth account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email:    formData.email,
        password: formData.password,
      });
      if (authError) throw new Error(authError.message);
      const authId = authData.user?.id;
      if (!authId) throw new Error('Account creation failed. Please try again.');

      // Step 2a — establish session so RLS resolves auth.uid()
      if (authData.session) {
        await supabase.auth.setSession({
          access_token:  authData.session.access_token,
          refresh_token: authData.session.refresh_token,
        });
      }

      // Step 3 — auto assign side for PA/IA
      const side = formData.licenseType === 'PA' ? 'Policyholder'
                 : formData.licenseType === 'IA' ? 'Carrier'
                 : formData.side;

      // Step 4 — find the user row auto-created by the on_auth_user_created
      // trigger (runs SECURITY DEFINER, so it exists even before the auth
      // session/auth.uid() is available), then mark signup as confirmed.
      let userId = null;
      for (let attempt = 0; attempt < 5 && !userId; attempt++) {
        const { data: existingUser } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', authId)
          .maybeSingle();
        if (existingUser) { userId = existingUser.id; break; }
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      if (!userId) throw new Error('Account setup is still finishing — please try again in a moment.');

      const { error: userError } = await supabase
        .from('users')
        .update({
          account_status:      'active',
          signup_confirmed:    true,
          signup_confirmed_at: new Date().toISOString(),
        })
        .eq('id', userId);
      if (userError) throw new Error('Failed to finish account setup: ' + userError.message);

      // Step 5 — insert user role
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, primary_role: formData.primaryRole, side });
      if (roleError) throw new Error('Failed to save role: ' + roleError.message);

      // Step 6 — insert licenses from NIPR (one at a time to avoid bulk conflict)
      // Will be populated when NIPR API credentials are obtained

     // Step 7 — update profile (row already created by the
     // on_auth_user_created trigger)
      const verificationStatus = 'unverified';

      const fullName = [
        formData.firstName,
        formData.middleName,
        formData.lastName,
        formData.nameSuffix,
      ].filter(Boolean).join(' ');

      const { error: profileError } = await supabase
        .from('profiles')
       .update({
         display_name:   fullName,
         name_suffix:    formData.nameSuffix || null,
         profile_source: 'manual',
         verification_badge: verificationStatus,
        is_claimed:     true,
       })
       .eq('user_id', userId);
      if (profileError) throw new Error('Failed to update profile: ' + profileError.message);

      // Step 7a — store encrypted legal name (non-blocking, session may not be ready yet)
      try {
        const { error: nameError } = await supabase.rpc('update_my_name', {
          p_first_name:  formData.firstName,
          p_middle_name: formData.middleName || null,
          p_last_name:   formData.lastName,
        });
        if (nameError) console.log('Name encryption failed silently:', nameError.message);
      } catch (nameErr) {
        console.log('Name encryption failed silently:', nameErr.message);
      }

      // Step 8 — audit log (non-blocking)
      try {
        await supabase
          .from('signup_audit')
          .insert({
            user_id:    userId,
            event_type: 'signup_attestation_confirmed',
            detail: {
              license_type:  formData.licenseType,
              primary_role:  formData.primaryRole,
              home_state:    formData.homeState,
            },
          });
      } catch (auditErr) {
        console.log('Audit log failed silently:', auditErr.message);
      }

      // Step 9 — if claiming a nominated profile, send the confirmation email;
      //          otherwise show the normal welcome modal
      if (isClaimingProfile && nominatedProfile) {
        const { data: newProfileRow } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', userId)
          .single();

        if (newProfileRow) {
          await supabase.functions.invoke('send-claim-email', {
            body: {
              nominated_profile_id: nominatedProfile.profile_id,
              new_user_id:          userId,
              new_profile_id:       newProfileRow.id,
              user_email:           formData.email,
              user_name:            fullName,
            },
          });
        }
        setShowClaimEmailSent(true);
      } else {
        setShowWelcomeModal(true);
      }

    } catch (err) {
      console.error('Signup failed:', err);
      setSubmitError(err.message || 'Something went wrong. Please try again or contact admin@umpire-iq.com');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── STEP ROUTER ──────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (steps[currentStep]) {
      case 'Identity': return <StepIdentity data={formData} onChange={updateField} onNext={handleIdentityNext} loading={checkingEmail} error={submitError} />;
      case 'License':  return <StepLicenseType data={formData} onChange={updateField} onNext={next} onBack={back} />;
      case 'Side':     return <StepSide data={formData} onChange={updateField} onNext={next} onBack={back} />;
      case 'Role':     return <StepRole data={formData} onChange={updateField} onNext={next} onBack={back} stepNum={needsSide ? 4 : 3} />;
      case 'State':    return <StepState data={formData} onChange={updateField} onNext={next} onBack={back} stepNum={needsSide ? 5 : 4} />;
      case 'Attest':   return <StepAttest data={formData} onBack={back} onSubmit={handleSubmit} loading={submitting} submitError={submitError} />;
      default: return null;
    }
  };

  if (showClaimEmailSent) {
    return (
      <div className="signup-page">
        <nav className="signup-nav">
          <div className="signup-nav-logo">Umpire<span>IQ</span></div>
        </nav>
        <div className="signup-body">
          <div className="signup-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📬</div>
            <div className="signup-step-eyebrow">One more step</div>
            <h2>Check your email</h2>
            <p style={{ fontSize: '15px', color: 'var(--muted)', lineHeight: 1.7, fontWeight: 300, margin: '16px 0 24px' }}>
              We've sent a confirmation link to{' '}
              <strong style={{ color: 'var(--foreground)' }}>{formData.email}</strong>.
              Click the link in that email to claim your existing profile and access your dashboard.
            </p>
            <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.7, fontWeight: 300, margin: '0 0 24px' }}>
              You can close this tab. The link expires in 3 hours.<br />
              Didn't get it? Check your spam or contact{' '}
              <a href="mailto:admin@umpire-iq.com" style={{ color: 'var(--gold)' }}>admin@umpire-iq.com</a>.
            </p>
            <button
              onClick={() => onNavigate('dashboard')}
              style={{ background: 'none', border: 'none', fontFamily: 'var(--sans)', fontSize: '14px', color: 'var(--muted)', cursor: 'pointer', padding: '10px' }}
            >
              Go to dashboard anyway →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      {showClaimModal && nominatedProfile && (
        <ClaimProfileModal
          profile={nominatedProfile}
          onClaim={() => { setIsClaimingProfile(true); setShowClaimModal(false); next(); }}
          onSkip={() => { setNominatedProfile(null); setIsClaimingProfile(false); setShowClaimModal(false); next(); }}
        />
      )}
      {showWelcomeModal && (
        <WelcomeModal
          onGetVerified={() => onNavigate('dashboard')}
          onDismiss={() => onNavigate('dashboard')}
        />
      )}
      <nav className="signup-nav">
        <div className="signup-nav-logo" onClick={() => onNavigate('home')}>Umpire<span>IQ</span></div>
        <span className="signup-nav-login">
          Already have an account?
          <a href="#" onClick={e => { e.preventDefault(); onNavigate('login'); }}> Sign in</a>
        </span>
      </nav>
      <ProgressBar steps={steps} currentStep={currentStep} />
      <div className="signup-body">
        <div className="signup-card">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default Signup;
